import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  SlidersHorizontal,
  X,
  ChevronDown,
  Plus
} from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { cachedGet, getCached } from '../lib/apiCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 8;

const getImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return url;
  }

  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

/* ==========================================================
 * Wrapper: remontează conținutul la fiecare schimbare de slug,
 * astfel încât toate filtrele / prețurile / paginile se resetează.
 * ========================================================== */
const CategoryPage = () => {
  const { slug } = useParams();
  return <CategoryPageContent key={slug} slug={slug} />;
};

const CategoryPageContent = ({ slug }) => {
  const { language, t } = useLanguage();
  const isRu = language === 'ru';
  const linkPrefix = isRu ? '/ru' : '';

  const cachedCats = getCached('/api/categories') || [];

  const [allCategories, setAllCategories] = useState(cachedCats);
  const [categoriesLoaded, setCategoriesLoaded] = useState(
    cachedCats.length > 0
  );
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(false);

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [availableBrandIds, setAvailableBrandIds] = useState([]);

  const [filterOpen, setFilterOpen] = useState(false);

  const [maxCategoryPrice, setMaxCategoryPrice] = useState(0);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [tempPriceRange, setTempPriceRange] = useState({ min: 0, max: 0 });
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [sortOrder, setSortOrder] = useState('');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  /* ==========================================================
   * DATE DERIVATE DIN CATEGORII
   * ========================================================== */
  const category = useMemo(
    () =>
      allCategories.find(
        (c) => c.slug === slug || c.slugRu === slug || c.id === slug
      ) || null,
    [allCategories, slug]
  );

  const breadcrumbPath = useMemo(() => {
    const path = [];
    let temp = category;
    const guard = new Set();

    while (temp && !guard.has(temp.id)) {
      guard.add(temp.id);
      path.unshift(temp);
      const parentId = temp.parentId;
      temp = parentId ? allCategories.find((c) => c.id === parentId) : null;
    }

    return path;
  }, [category, allCategories]);

  // Copiii direcți ai categoriei curente
  const children = useMemo(
    () =>
      category
        ? allCategories.filter((c) => c.parentId === category.id)
        : [],
    [category, allCategories]
  );

  // Dacă are copii => nivel 1 / 2 (grilă). Altfel => nivel 3 (produse).
  const isParentCategory = children.length > 0;

  // Taburi (doar pentru pagina cu produse): categoriile cu același părinte
  const tabs = useMemo(() => {
    if (!category || isParentCategory || !category.parentId) return [];
    const siblings = allCategories.filter(
      (c) => c.parentId === category.parentId
    );
    return siblings.length > 0 ? siblings : [category];
  }, [category, isParentCategory, allCategories]);

  const getCatSlug = (c) =>
    isRu && c.slugRu ? c.slugRu : c.slug || c.id;

  const getCatName = (c) => (isRu && c.nameRu ? c.nameRu : c.name);

  /* ==========================================================
   * FETCH CATEGORII + BRANDURI
   * ========================================================== */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [catsData, brandsData] = await Promise.all([
          cachedGet('/api/categories'),
          cachedGet('/api/brands')
        ]);

        if (cancelled) return;

        setAllCategories(catsData || []);
        setBrands(brandsData || []);
        setCategoriesLoaded(true);
      } catch (err) {
        console.error('Error loading category:', err);
        if (cancelled) return;
        setError(true);
        setCategoriesLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  /* ==========================================================
   * FETCH PRODUSE (doar pentru categorii fără subcategorii)
   * ========================================================== */
  const brandsKey = selectedBrands.join(',');

  useEffect(() => {
    if (!slug) return;
    if (isParentCategory) return;
    if (categoriesLoaded && !category) return;

    let cancelled = false;

    (async () => {
      try {
        const params = new URLSearchParams({
          categorySlug: slug,
          page: String(currentPage),
          pageSize: String(PAGE_SIZE)
        });

        if (priceRange.min > 0) {
          params.append('minPrice', String(priceRange.min));
        }

        if (priceRange.max > 0 && priceRange.max < maxCategoryPrice) {
          params.append('maxPrice', String(priceRange.max));
        }

        if (brandsKey) {
          params.append('brandIds', brandsKey);
        }

        if (sortOrder) {
          params.append('sortBy', 'price');
          params.append('sortOrder', sortOrder);
        }

        const data = await cachedGet(
          `/api/products/list/paginated?${params.toString()}`
        );

        if (cancelled) return;

        setProducts(data.items || []);
        setTotalProducts(data.total || 0);
        setAvailableBrandIds(data.availableBrandIds || []);

        if (maxCategoryPrice === 0 && data.maxPrice > 0) {
          setMaxCategoryPrice(data.maxPrice);
          setPriceRange({ min: 0, max: data.maxPrice });
          setTempPriceRange({ min: 0, max: data.maxPrice });
        }
      } catch (err) {
        if (!cancelled) console.error('Error fetching products:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slug,
    isParentCategory,
    categoriesLoaded,
    !!category,
    currentPage,
    priceRange.min,
    priceRange.max,
    brandsKey,
    sortOrder
  ]);

  /* ==========================================================
   * HANDLERE FILTRE (resetează pagina direct aici)
   * ========================================================== */
  const handlePriceRangeApply = () => {
    setPriceRange(tempPriceRange);
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    setSortDropdownOpen(false);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setPriceRange({ min: 0, max: maxCategoryPrice });
    setTempPriceRange({ min: 0, max: maxCategoryPrice });
    setSelectedBrands([]);
    setSortOrder('');
    setSortDropdownOpen(false);
    setCurrentPage(1);
  };

  const handleBrandToggle = (brandId) => {
    setSelectedBrands((prev) =>
      prev.includes(brandId)
        ? prev.filter((b) => b !== brandId)
        : [...prev, brandId]
    );
    setCurrentPage(1);
  };

  const clampPrice = (value) => {
    const max = maxCategoryPrice || 0;
    return Math.min(Math.max(Number(value), 0), max);
  };

  const handleMinPriceChange = (value) => {
    const nextMin = clampPrice(value);
    const safeMin = Math.min(nextMin, tempPriceRange.max - 10);

    setTempPriceRange((prev) => ({
      ...prev,
      min: Math.max(0, safeMin)
    }));
  };

  const handleMaxPriceChange = (value) => {
    const nextMax = clampPrice(value);
    const safeMax = Math.max(nextMax, tempPriceRange.min + 10);

    setTempPriceRange((prev) => ({
      ...prev,
      max: Math.min(maxCategoryPrice || 0, safeMax)
    }));
  };

  const getPercent = (value) => {
    if (!maxCategoryPrice) return 0;
    return Math.round((value / maxCategoryPrice) * 100);
  };

  /* ==========================================================
   * TEXTE
   * ========================================================== */
  const sortOptions = [
    { value: '', label: isRu ? 'По умолчанию' : 'Implicit' },
    {
      value: 'asc',
      label: isRu ? 'Цена: от низкой к высокой' : 'Preț: mic → mare'
    },
    {
      value: 'desc',
      label: isRu ? 'Цена: от высокой к низкой' : 'Preț: mare → mic'
    }
  ];

  const currentSortLabel =
    sortOptions.find((o) => o.value === sortOrder)?.label ||
    (isRu ? 'По умолчанию' : 'Implicit');

  /* ==========================================================
   * SLIDER PREȚ
   * ========================================================== */
  const renderPriceSlider = (isMobile = false) => {
    const currentMax = tempPriceRange.max || maxCategoryPrice;
    const displayMax =
      currentMax >= maxCategoryPrice - 10 ? maxCategoryPrice : currentMax;

    const minPercent = getPercent(tempPriceRange.min);
    const maxPercent =
      displayMax >= maxCategoryPrice ? 100 : getPercent(displayMax);

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h4 className="font-bold text-gray-900">
            {t('categoryPage.price') || (isRu ? 'Цена' : 'Preț')}
          </h4>

          <div className="rounded-full bg-[#a7cf26]/10 px-3 py-1 text-xs font-black text-[#7fa20f]">
            {tempPriceRange.min || 0} - {displayMax} MDL
          </div>
        </div>

        <div className="relative pt-5 pb-4">
          <div className="relative h-[24px]">
            <div className="absolute left-0 right-0 top-1/2 h-[8px] -translate-y-1/2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="absolute top-0 h-full rounded-full bg-gradient-to-r from-[#a7cf26] to-[#8fb71d]"
                style={{
                  left: `${minPercent}%`,
                  right: `${100 - maxPercent}%`
                }}
              />
            </div>

            <input
              type="range"
              min="0"
              max={maxCategoryPrice || 0}
              step="10"
              value={tempPriceRange.min || 0}
              onChange={(e) => handleMinPriceChange(e.target.value)}
              className="price-range price-range-min pointer-events-none absolute inset-y-0 -left-[12px] w-[calc(100%+24px)] h-[24px] appearance-none bg-transparent"
              style={{
                zIndex:
                  tempPriceRange.min > (tempPriceRange.max || 0) - 50 ? 5 : 3
              }}
            />

            <input
              type="range"
              min="0"
              max={maxCategoryPrice || 0}
              step="10"
              value={tempPriceRange.max || maxCategoryPrice || 0}
              onChange={(e) => handleMaxPriceChange(e.target.value)}
              className="price-range price-range-max pointer-events-none absolute inset-y-0 -left-[12px] w-[calc(100%+24px)] h-[24px] appearance-none bg-transparent"
              style={{ zIndex: 4 }}
            />
          </div>

          <style>{`
            .price-range {
              -webkit-appearance: none;
              appearance: none;
              background: transparent;
            }

            .price-range::-webkit-slider-runnable-track {
              height: 8px;
              background: transparent;
              border: none;
            }

            .price-range::-moz-range-track {
              height: 8px;
              background: transparent;
              border: none;
            }

            .price-range::-webkit-slider-thumb {
              pointer-events: auto;
              -webkit-appearance: none;
              appearance: none;
              width: 24px;
              height: 24px;
              border-radius: 9999px;
              background: #a7cf26;
              border: 6px solid #ffffff;
              box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
              cursor: pointer;
              margin-top: -8px;
            }

            .price-range::-moz-range-thumb {
              pointer-events: auto;
              width: 24px;
              height: 24px;
              border-radius: 9999px;
              background: #a7cf26;
              border: 6px solid #ffffff;
              box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
              cursor: pointer;
            }

            .price-range:focus {
              outline: none;
            }
          `}</style>
        </div>

        <div className="flex items-center justify-between text-xs font-semibold text-gray-500">
          <span>0 MDL</span>
          <span>{maxCategoryPrice} MDL</span>
        </div>

        <button
          onClick={() => {
            handlePriceRangeApply();
            if (isMobile) setFilterOpen(false);
          }}
          className="w-full bg-[#a7cf26] text-white py-2.5 rounded-2xl hover:bg-[#96bc21] transition font-semibold"
        >
          {t('categoryPage.apply') || (isRu ? 'Применить' : 'Aplică')}
        </button>
      </div>
    );
  };

  /* ==========================================================
   * CONȚINUT FILTRE (comun desktop + mobil)
   * ========================================================== */
  const visibleBrands = availableBrandIds.length
    ? brands.filter((b) => availableBrandIds.includes(b.id))
    : [];

  const renderFilterContent = (isMobile = false) => (
    <>
      {/* SORTARE */}
      <div className="mb-6 pb-6 border-b">
        <h4 className="font-bold text-gray-900 mb-4">
          {isRu ? 'Сортировка' : 'Sortare'}
        </h4>

        <div className="relative">
          <button
            type="button"
            onClick={() => setSortDropdownOpen((prev) => !prev)}
            aria-expanded={sortDropdownOpen}
            className="w-full bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 text-left hover:border-[#a7cf26]/90 focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90 transition"
          >
            <span className="font-semibold text-gray-700">
              {currentSortLabel}
            </span>

            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform ${
                sortDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {sortDropdownOpen && (
            <div
              className={`${
                isMobile ? 'mt-2' : 'absolute z-30 mt-2'
              } w-full bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden`}
            >
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSortChange(option.value)}
                  className={`w-full px-4 py-3 text-left font-medium transition ${
                    sortOrder === option.value
                      ? 'bg-[#a7cf26] text-white'
                      : 'text-gray-700 hover:bg-[#a7cf26]/10 hover:text-[#a7cf26]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PREȚ */}
      <div className="mb-6 pb-6 border-b">{renderPriceSlider(isMobile)}</div>

      {/* BRANDURI */}
      {visibleBrands.length > 0 && (
        <div>
          <h4 className="font-bold text-gray-900 mb-4">
            {t('categoryPage.brand') || 'Brand'}
          </h4>

          <div
            className={`space-y-3 ${
              isMobile ? '' : 'max-h-64 overflow-y-auto pr-1'
            }`}
          >
            {visibleBrands.map((brand) => (
              <label
                key={brand.id}
                className="flex items-center gap-3 cursor-pointer group rounded-2xl hover:bg-gray-50 p-2 transition"
              >
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(brand.id)}
                  onChange={() => handleBrandToggle(brand.id)}
                  className="w-4 h-4 text-[#a7cf26] border-gray-300 rounded focus:ring-[#a7cf26]/90"
                />

                <div className="flex items-center gap-3 flex-1">
                  {brand.logo && (
                    <img
                      src={getImageUrl(brand.logo)}
                      alt={brand.name}
                      className="w-12 h-9 object-contain"
                    />
                  )}

                  <span className="text-gray-700 group-hover:text-[#a7cf26] transition font-medium">
                    {brand.name}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );

  /* ==========================================================
   * SEO / TEXTE CATEGORIE
   * ========================================================== */
  const categoryName = category ? getCatName(category) : '';

  const categoryDescription =
    isRu && category?.descriptionRu
      ? category.descriptionRu
      : category?.description;

  const categoryFaqs =
    category?.faqs && category.faqs.length > 0
      ? category.faqs
          .map((faq) => ({
            question: isRu && faq.questionRu ? faq.questionRu : faq.question,
            answer: isRu && faq.answerRu ? faq.answerRu : faq.answer
          }))
          .filter((faq) => faq.question && faq.answer)
      : [];

  const seoTitle =
    isRu && category?.seoTitleRu
      ? category.seoTitleRu
      : category?.seoTitle || categoryName;

  const seoDescription =
    isRu && category?.seoDescriptionRu
      ? category.seoDescriptionRu
      : category?.seoDescription ||
        (isRu
          ? `${categoryName} – купить онлайн на AgroSmart.md по лучшей цене в Молдове. Быстрая доставка, оплата в 3 рата.`
          : `${categoryName} – produse disponibile pe AgroSmart.md. Cumpără online ${categoryName?.toLowerCase()} la cel mai bun preț din Moldova. Livrare rapidă, achitare în 3 rate.`);

  const categoryImage = getImageUrl(category?.image || category?.icon);

  /* ==========================================================
   * TABURI (nivel 3 - pagina cu produse)
   * ========================================================== */
  const renderCategoryTabs = () => {
    if (tabs.length === 0) return null;

    return (
      <div className="w-full">
        <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
          {tabs.map((tab) => {
            const tabName = getCatName(tab);
            const tabImage = getImageUrl(tab.image || tab.icon);
            const isActive = tab.id === category?.id;

            return (
              <Link
                key={tab.id}
                to={`${linkPrefix}/category/${getCatSlug(tab)}`}
                data-testid={`category-tab-${tab.id}`}
                className={`group inline-flex items-center gap-1.5 md:gap-2 rounded-[12px] md:rounded-[14px] px-2 md:px-2.5 py-1.5 md:py-2 transition-all duration-200 max-w-full ${
                  isActive
                    ? 'bg-[#a7cf26] text-white shadow-sm'
                    : 'bg-[#f0f1f5] hover:bg-[#e7e9ef] text-gray-700 hover:-translate-y-0.5 hover:shadow-sm'
                }`}
              >
                <div
                  className={`w-7 h-7 md:w-8 md:h-8 rounded-md md:rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${
                    isActive ? 'bg-white/95' : 'bg-white'
                  }`}
                >
                  {tabImage ? (
                    <img
                      src={tabImage}
                      alt={tabName}
                      className="w-full h-full object-contain p-0.5 group-hover:scale-110 transition-transform duration-200"
                    />
                  ) : (
                    <span className="text-[11px] md:text-xs font-bold text-[#7fa20f]">
                      {tabName?.[0] || '?'}
                    </span>
                  )}
                </div>

                <span
                  className={`text-[11px] md:text-[12px] xl:text-[14px] font-semibold leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px] md:max-w-[220px] ${
                    isActive
                      ? 'text-white'
                      : 'text-gray-700 group-hover:text-[#7fa20f]'
                  }`}
                >
                  {tabName}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  /* ==========================================================
   * DESCRIERE + FAQ (comun)
   * ========================================================== */
  const renderCategoryBottomContent = () => {
    if (!categoryDescription && categoryFaqs.length === 0) return null;

    return (
      <div className="w-full px-3 md:px-6 pb-12">
        <div className="bg-white rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-sm p-5 md:p-8 space-y-8">
          {categoryDescription && (
            <section>
              <div
                className="category-description text-[15px] md:text-[16px] leading-relaxed text-gray-700"
                dangerouslySetInnerHTML={{ __html: categoryDescription }}
              />
            </section>
          )}

          <style>{`
            .category-description p {
              margin-bottom: 1rem;
            }

            .category-description p:last-child {
              margin-bottom: 0;
            }

            .category-description strong {
              font-weight: 700;
            }

            .category-description em {
              font-style: italic;
            }

            .category-description ul {
              list-style: disc;
              padding-left: 1.5rem;
              margin: 1rem 0;
            }

            .category-description ol {
              list-style: decimal;
              padding-left: 1.5rem;
              margin: 1rem 0;
            }

            .category-description li {
              margin-bottom: 0.35rem;
            }

            .category-description h1,
            .category-description h2,
            .category-description h3 {
              font-weight: 800;
              color: #111827;
              margin-top: 1.25rem;
              margin-bottom: 0.75rem;
            }

            .category-description a {
              text-decoration: underline;
            }
          `}</style>

          {categoryFaqs.length > 0 && (
            <section className={categoryDescription ? 'border-t pt-6' : ''}>
              <div className="space-y-3">
                {categoryFaqs.map((item, index) => {
                  const isOpen = openFaqIndex === index;

                  return (
                    <div
                      key={index}
                      className="border border-gray-100 rounded-2xl overflow-hidden bg-gray-50"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                      >
                        <span className="text-[14px] md:text-[16px] font-bold text-gray-900">
                          {item.question}
                        </span>

                        <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                          <Plus
                            className={`w-4 h-4 text-[#a7cf26] transition-transform duration-300 ${
                              isOpen ? 'rotate-45' : 'rotate-0'
                            }`}
                          />
                        </span>
                      </button>

                      <div
                        className={`grid transition-all duration-300 ease-in-out ${
                          isOpen
                            ? 'grid-rows-[1fr] opacity-100'
                            : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 pb-4 text-[14px] md:text-[15px] leading-relaxed text-gray-600 whitespace-pre-line">
                            {item.answer}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    );
  };

  /* ==========================================================
   * PAGINARE
   * ========================================================== */
  const renderPagination = () => {
    const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));

    if (totalPages <= 1) return null;

    const PAGE_GROUP_SIZE = 5;
    const currentGroup = Math.floor((currentPage - 1) / PAGE_GROUP_SIZE);
    const groupStart = currentGroup * PAGE_GROUP_SIZE + 1;
    const groupEnd = Math.min(groupStart + PAGE_GROUP_SIZE - 1, totalPages);

    const visiblePages = [];
    for (let p = groupStart; p <= groupEnd; p++) {
      visiblePages.push(p);
    }

    const goTo = (n) => {
      setCurrentPage(n);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-600">
          {t('categoryPage.showing') || 'Afișare'}{' '}
          <span className="font-semibold">
            {(currentPage - 1) * PAGE_SIZE + 1}
          </span>{' '}
          -{' '}
          <span className="font-semibold">
            {Math.min(currentPage * PAGE_SIZE, totalProducts)}
          </span>{' '}
          {t('categoryPage.from') || 'din'}{' '}
          <span className="font-semibold">{totalProducts}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => goTo(Math.max(1, groupStart - PAGE_GROUP_SIZE))}
            disabled={groupStart === 1}
            className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('categoryPage.prev') || 'Anterior'}
          </button>

          <div className="flex gap-1">
            {visiblePages.map((p) => (
              <button
                key={p}
                onClick={() => goTo(p)}
                className={`w-10 h-10 rounded-lg font-semibold transition ${
                  currentPage === p
                    ? 'bg-[#a7cf26] text-white'
                    : 'border border-gray-300 hover:bg-gray-100'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            onClick={() => goTo(Math.min(totalPages, groupEnd + 1))}
            disabled={groupEnd >= totalPages}
            className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {t('categoryPage.next') || 'Următor'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  /* ==========================================================
   * LOADING / 404
   * ========================================================== */
  if (!categoriesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-[#a7cf26] animate-spin" />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>

          <p className="text-xl text-gray-600 mb-6">
            {t('categoryPage.notFound') ||
              (isRu ? 'Категория не найдена' : 'Categoria nu a fost găsită')}
          </p>

          <Link
            to={linkPrefix || '/'}
            className="bg-[#a7cf26] text-white px-6 py-3 rounded-xl transition font-semibold inline-block"
          >
            {t('categoryPage.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  /* ==========================================================
   * RENDER
   * ========================================================== */
  const seoBreadcrumbs = [
    { name: isRu ? 'Главная' : 'Acasă', url: linkPrefix || '/' },
    ...breadcrumbPath.map((item) => ({
      name: getCatName(item),
      url: `${linkPrefix}/category/${getCatSlug(item)}`
    }))
  ];

  return (
    <div className="relative min-h-screen bg-gray-50">
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={categoryImage || undefined}
        breadcrumbs={seoBreadcrumbs}
      />

      {/* =========================
          BREADCRUMB
      ========================== */}
      <div className="bg-white border-b">
        <div className="w-full px-3 md:px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <Link to={linkPrefix || '/'} className="hover:text-[#a7cf26]">
              {t('categoryPage.breadcrumb.home')}
            </Link>

            {breadcrumbPath.map((item, index) => {
              const isLast = index === breadcrumbPath.length - 1;
              const name = getCatName(item);

              return (
                <React.Fragment key={item.id}>
                  <ChevronRight className="w-4 h-4" />

                  {isLast ? (
                    <span className="text-gray-900 font-semibold">{name}</span>
                  ) : (
                    <Link
                      to={`${linkPrefix}/category/${getCatSlug(item)}`}
                      className="hover:text-[#a7cf26]"
                    >
                      {name}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {isParentCategory ? (
        /* ====================================================
         * NIVEL 1 / 2 -> GRILĂ CU SUBCATEGORII (imagine + nume)
         * ==================================================== */
        <>
          <div className="w-full px-3 md:px-6 py-8 md:py-10">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 mb-6">
              {categoryName}
            </h1>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5">
              {children.map((sub) => {
                const subName = getCatName(sub);

                return (
                  <Link
                    key={sub.id}
                    to={`${linkPrefix}/category/${getCatSlug(sub)}`}
                    data-testid={`subcategory-card-${sub.id}`}
                    className="group bg-white rounded-2xl border border-gray-100 p-3 md:p-4 flex flex-col items-center text-center transition hover:shadow-lg hover:border-brand-200 hover:-translate-y-0.5"
                  >
                    <div className="w-full aspect-square rounded-xl bg-gray-50 overflow-hidden flex items-center justify-center mb-3">
                      {sub.image || sub.icon ? (
                        <img
                          src={getImageUrl(sub.image || sub.icon)}
                          alt={subName}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center text-lg font-bold">
                          {subName?.[0] || '?'}
                        </div>
                      )}
                    </div>

                    <span className="text-sm md:text-[15px] font-semibold text-gray-800 group-hover:text-brand-700 transition leading-tight line-clamp-2">
                      {subName}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          {renderCategoryBottomContent()}
        </>
      ) : (
        /* ====================================================
         * NIVEL 3 -> TITLU + TABURI + PRODUSE + FILTRE
         * ==================================================== */
        <>
          <div className="w-full px-3 md:px-6 pt-5 md:pt-7 pb-4">
            <h1 className="text-2xl md:text-3xl xl:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
              {categoryName}
            </h1>
          </div>

          <div className="w-full px-3 md:px-6 pb-6 md:pb-8">
            <div className="flex gap-8">
              {/* DESKTOP FILTER SIDEBAR */}
              <aside className="hidden lg:block w-80 flex-shrink-0">
                <div className="bg-white rounded-[28px] p-6 border border-gray-100 sticky top-24 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <SlidersHorizontal className="w-5 h-5 text-[#a7cf26]" />
                      {t('categoryPage.filters')}
                    </h3>

                    <button
                      onClick={resetFilters}
                      className="text-sm text-[#a7cf26]/90 hover:text-[#a7cf26] font-semibold"
                    >
                      {t('categoryPage.reset')}
                    </button>
                  </div>

                  {renderFilterContent(false)}
                </div>
              </aside>

              {/* MOBILE FILTER BUTTON */}
              <button
                onClick={() => setFilterOpen(true)}
                className="lg:hidden fixed bottom-[82px] left-4 bg-[#a7cf26]/90 text-white p-3.5 rounded-full shadow-lg hover:bg-[#a7cf26] transition z-40"
                aria-label={isRu ? 'Открыть фильтры' : 'Deschide filtrele'}
              >
                <SlidersHorizontal className="w-6 h-6" />
              </button>

              {/* MOBILE FILTER DRAWER */}
              {filterOpen && (
                <div
                  className="lg:hidden fixed inset-0 bg-black/50 z-50"
                  onClick={() => setFilterOpen(false)}
                >
                  <div
                    className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900">
                          {t('categoryPage.filters')}
                        </h3>

                        <button onClick={() => setFilterOpen(false)}>
                          <X className="w-6 h-6" />
                        </button>
                      </div>

                      {renderFilterContent(true)}

                      <button
                        onClick={() => {
                          resetFilters();
                          setFilterOpen(false);
                        }}
                        className="w-full mt-6 bg-gray-200 text-gray-700 py-2.5 rounded-2xl hover:bg-gray-300 transition font-semibold"
                      >
                        {t('categoryPage.resetFilters')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* PRODUCTS AREA */}
              <div className="flex-1 min-w-0">
                {tabs.length > 0 && (
                  <div className="mb-5 md:mb-6">{renderCategoryTabs()}</div>
                )}

                <div className="flex justify-end mb-4">
                  <span className="text-sm text-gray-500">
                    {totalProducts} {t('categoryPage.productsAvailable')}
                  </span>
                </div>

                {products.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <p className="text-xl text-gray-600">
                      {t('categoryPage.empty')}
                    </p>

                    <button
                      onClick={resetFilters}
                      className="mt-4 text-[#a7cf26]/90 hover:text-[#a7cf26] font-semibold"
                    >
                      {t('categoryPage.resetFilters')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
                      {products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>

                    {renderPagination()}
                  </>
                )}
              </div>
            </div>
          </div>

          {renderCategoryBottomContent()}
        </>
      )}
    </div>
  );
};

export default CategoryPage;